import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';

import { Course } from '../../../models/course.model';
import { CourseType } from '../../../models/course-type.model';
import { Location } from '../../../models/location.model';
import { CalendarModule } from 'primeng/calendar';
import { FloatLabelModule } from 'primeng/floatlabel';
import { CourseService } from '../../../services/course.service';
import { NotificationService } from '../../../services/notification.service';
import { CourseTypeService } from '../../../services/coursetype.service';
import { LocationService } from '../../../services/location.service';
import { InputTextModule } from 'primeng/inputtext';
import { DialogManagerService } from '../../../services/dialog-manager.service';
import { AuthService } from '../../../services/auth.service';
import { LernfeldContentService } from '../../../services/lernfeld-content.service';
import { Occupation } from '../../../models/lernfeld-content.model';
import { LERNFELD_PERMISSIONS } from '../../menu/lernfelder/lernfeld-permissions';
@Component({
  selector: 'app-create-course-dialog',
  standalone: true,
  imports: [
    ButtonModule,
    CheckboxModule,
    DropdownModule,
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    CalendarModule,
    FloatLabelModule,
    InputTextModule,
  ],
  templateUrl: './create-course-dialog.component.html',
  styleUrl: './create-course-dialog.component.scss',
})
export class CreateCourseDialogComponent {
  locations: Location[] = [];
  courseTypes: CourseType[] = [];
  occupations: Occupation[] = [];
  timetableNameOptions: string[] = [];

  newCourseForm: FormGroup;

  constructor(
    private authService: AuthService,
    private lernfeldContentService: LernfeldContentService,
    private formBuilder: FormBuilder,
    private courseService: CourseService,
    private notificationService: NotificationService,
    private courseTypeService: CourseTypeService,
    private locationService: LocationService,
    private dialogManager: DialogManagerService,
  ) {
    this.newCourseForm = this.formBuilder.group({
      courseName: ['', Validators.required],
      timetableName: [null],
      startDate: ['', Validators.required],
      endDate: [''],
      locationId: ['', Validators.required],
      courseTypeId: ['', Validators.required],
      occupationId: [null],
      ihk: [''],
      isActive: [true],
      apt1Date: [null],
      apt2Date: [null],
    });
  }

  ngOnInit(): void {
    this.loadCourseTypes();
    this.loadLocations();
    this.loadOccupations();
    this.loadTimetableNameOptions();
  }

  loadTimetableNameOptions(): void {
    this.courseService.getTimetableNameOptions().subscribe({
      next: (data) => (this.timetableNameOptions = data),
      error: () => console.error('Stundenplan-Namen nicht geladen!'),
    });
  }

  // Liste der Berufe liegt hinter Lernfeld.Read; ohne das Recht kein Feld
  get canReadOccupations(): boolean {
    return this.authService
      .getUserPermissions()
      .includes(LERNFELD_PERMISSIONS.read);
  }

  loadOccupations(): void {
    if (!this.canReadOccupations) return;

    this.lernfeldContentService.getOccupations().subscribe({
      next: (data) =>
        (this.occupations = [...data].sort((a, b) =>
          a.name.localeCompare(b.name),
        )),
      error: () => console.error('Ausbildungsberufe nicht geladen!'),
    });
  }

  get courseType(): CourseType {
    const id = this.newCourseForm.get('courseTypeId').value;
    return this.courseTypes.find((c) => c.courseTypeId === id);
  }

  get location(): Location {
    const id = this.newCourseForm.get('locationId').value;
    return this.locations.find((l) => l.locationId === id);
  }

  loadCourseTypes(): void {
    this.courseTypeService.getCourseTypes().subscribe({
      next: (data) =>
        (this.courseTypes = data.sort((a, b) =>
          a.courseTypeName.localeCompare(b.courseTypeName),
        )),
      error: () => console.error('Kurstypen nicht geladen!'),
    });
  }

  loadLocations(): void {
    this.locationService.getLocations().subscribe({
      next: (data) =>
        (this.locations = data.sort((a, b) =>
          a.locationName.localeCompare(b.locationName),
        )),
      error: () => console.error('Standorte nicht geladen!'),
    });
  }

  closeDialog(): void {
    this.newCourseForm.reset({ isActive: true });
    this.dialogManager.closeCourseDialog();
  }

  saveCreatedCourse(): void {
    const newCourse: Course = {
      courseName: this.newCourseForm.get('courseName').value,
      startDate: this.newCourseForm.get('startDate').value,
      endDate:
        this.newCourseForm.get('endDate').value ||
        this.newCourseForm.get('startDate').value,
      locationId: this.newCourseForm.get('locationId').value,
      courseTypeId: this.newCourseForm.get('courseTypeId').value,
      occupationId: this.newCourseForm.get('occupationId').value,
      timetableName: this.newCourseForm.get('timetableName').value?.trim() || null,
      ihk: this.newCourseForm.get('ihk').value,
      isActive: this.newCourseForm.get('isActive').value,
      location: this.location,
      courseType: this.courseType,
      apt1Date: this.newCourseForm.get('apt1Date').value,
      apt2Date: this.newCourseForm.get('apt2Date').value,
    };
    this.courseService.createCourse(newCourse).subscribe({
      next: () => {
        this.notificationService.success(
          `Kurs ${newCourse.courseName} erfolgreich erstellt`,
        );
        this.closeDialog();
      },
      error: () => {
        this.notificationService.error(
          `Kurs ${newCourse.courseName} NICHT erstellt`,
        );
      },
    });
  }
}
